import { useState } from 'react';
import { useEmails, useEmailStats } from '../../hooks/useEmails';
import type { EmailFilters, EmailLog } from '../../lib/emailsApi';
import { EmailStatsOverview } from '../domain/emails/EmailStatsOverview';
import { EmailsFilters } from '../domain/emails/EmailsFilters';
import { EmailsTable } from '../domain/emails/EmailsTable';
import { EmailDetailModal } from '../domain/emails/EmailDetailModal';
import { FadeIn, SkeletonTable } from '../ui/Skeleton';
import { useI18n } from '../../contexts/I18nContext';

const DEFAULT_FILTERS: EmailFilters = {
  sort: 'sentAt',
  order: 'desc',
  page: 1,
  limit: 50,
};

/**
 * Email History (CLE-310) — overall + per-template open-rate stats and a
 * filterable/sortable log of every email the platform has sent.
 *
 * Lives in Settings rather than the top-level sidebar: it is an administrative
 * audit view, not day-to-day navigation. SettingsPage only renders this tab for
 * ADMIN, and `/api/admin/emails` is `authorizeRole('ADMIN')` server-side, so the
 * tab gate is a UX nicety and not the security border.
 */
export function EmailHistorySettings() {
  const { t } = useI18n();
  const [filters, setFilters] = useState<EmailFilters>(DEFAULT_FILTERS);
  const [selected, setSelected] = useState<EmailLog | null>(null);

  const stats = useEmailStats();
  const { data, isLoading, isError, error } = useEmails(filters);

  const emails = data?.data ?? [];
  const meta = data?.meta;
  const templates = stats.data?.byTemplate.map((tpl) => tpl.template) ?? [];

  function patchFilters(patch: Partial<EmailFilters>) {
    // Any filter change resets to page 1.
    setFilters((prev) => ({ ...prev, ...patch, page: 1 }));
  }

  function handleSort(field: string) {
    setFilters((prev) => ({
      ...prev,
      sort: field as EmailFilters['sort'],
      order: prev.sort === field && prev.order === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  }

  function clearFilters() {
    setFilters(DEFAULT_FILTERS);
  }

  return (
    <div>
      <p className="text-sm text-gray-500">{t('settings.emailHistory.intro')}</p>

      {/* Stats overview */}
      <div className="mt-6">
        {stats.isLoading && <SkeletonTable rows={3} columns={4} />}
        {stats.isError && (
          <p className="text-sm text-red-600">
            {t('settings.emailHistory.statsError')}
            {stats.error instanceof Error ? `: ${stats.error.message}` : ''}.
          </p>
        )}
        {stats.data && (
          <FadeIn>
            <EmailStatsOverview stats={stats.data} />
          </FadeIn>
        )}
      </div>

      {/* Filters */}
      <div className="mt-6">
        <EmailsFilters
          filters={filters}
          templates={templates}
          onChange={patchFilters}
          onClear={clearFilters}
        />
      </div>

      {/* Log table */}
      <div className="mt-6">
        {isLoading && <SkeletonTable rows={8} columns={8} />}
        {isError && (
          <p className="text-sm text-red-600">
            {t('settings.emailHistory.loadError')}
            {error instanceof Error ? `: ${error.message}` : ''}.
          </p>
        )}
        {!isLoading && !isError && (
          <FadeIn>
            <EmailsTable
              emails={emails}
              sort={filters.sort ?? 'sentAt'}
              order={filters.order ?? 'desc'}
              onSort={handleSort}
              onRowClick={setSelected}
            />

            {meta && meta.totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                <span>
                  {t('settings.emailHistory.pageOf', {
                    page: meta.page,
                    totalPages: meta.totalPages,
                    total: meta.total,
                  })}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={meta.page <= 1}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) - 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('settings.emailHistory.previous')}
                  </button>
                  <button
                    type="button"
                    disabled={meta.page >= meta.totalPages}
                    onClick={() => setFilters((p) => ({ ...p, page: (p.page ?? 1) + 1 }))}
                    className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
                  >
                    {t('settings.emailHistory.next')}
                  </button>
                </div>
              </div>
            )}
          </FadeIn>
        )}
      </div>

      {selected && <EmailDetailModal email={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
